import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Home, FilePlus2, ClipboardList, LogOut, Truck, Users } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { ToastHost } from '@/components/layout/ToastHost';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Layout mobile-first du portail commercial.
 *
 * Concept UX : le commercial arrive sur son téléphone, il voit tout de suite ce
 * qu'il doit faire aujourd'hui. Header épuré (sans sidebar), bottom-nav 4 tabs
 * pour un accès instantané au geste principal (« Nouvelle proforma »).
 *
 * Responsive : sur desktop (≥ 900px), la bottom-nav bascule en top-nav horizontale
 * dans la barre du haut ; le contenu reste large (max 900px) pour rester lisible.
 */
export const CommercialLayout = () => {
  const nav  = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-2, #F5F7FA)', color: 'var(--tx-1, #111827)',
    }}>
      {/* ─── Header ─── */}
      <header style={{
        background: '#fff', borderBottom: '1px solid var(--bd-1, #E5E7EB)',
        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, background: 'var(--pr-1, #1F4E79)',
            color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14,
          }}>
            LB
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Commercial · LEAD BETON</div>
            <div style={{ fontSize: 11, color: 'var(--tx-3, #6B7280)' }}>{user?.fullName}</div>
          </div>
        </div>
        <button
          onClick={() => { logout(); nav('/login'); }}
          aria-label="Se déconnecter"
          style={{
            background: 'transparent', border: '1px solid var(--bd-1, #E5E7EB)',
            borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--tx-2, #374151)',
            fontSize: 13,
          }}
        >
          <LogOut size={14} /> Sortir
        </button>
      </header>

      {/* ─── Contenu ─── */}
      <main style={{
        flex: 1, padding: '16px 16px 96px 16px', // bottom padding pour la nav
        maxWidth: 900, width: '100%', margin: '0 auto',
      }}>
        <Outlet />
      </main>

      {/* ─── Bottom nav (mobile-first, responsive top sur ≥ 900px) ─── */}
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff', borderTop: '1px solid var(--bd-1, #E5E7EB)',
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
        boxShadow: '0 -6px 20px -8px rgba(0,0,0,0.15)',
        zIndex: 20,
      }}>
        <NavItem to="/commercial"           icon={<Home size={18} />}          label="Aujourd'hui" end />
        <NavItem to="/commercial/clients"   icon={<Users size={18} />}         label="Clients" />
        <NavItem to="/commercial/nouvelle"  icon={<FilePlus2 size={18} />}     label="Nouvelle" />
        <NavItem to="/commercial/proformas" icon={<ClipboardList size={18} />} label="Proformas" />
        <NavItem to="/commercial/commandes" icon={<Truck size={18} />}         label="Commandes" />
      </nav>

      {/* Toasts globaux — sinon les feedbacks des mutations TanStack Query
          restent silencieux dans le portail commercial (bug remonté par CTO). */}
      <ToastHost />
    </div>
  );
};

function NavItem({ to, icon, label, end }: { to: string; icon: React.ReactNode; label: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '10px 4px 12px 4px', gap: 2,
        color: isActive ? 'var(--pr-1, #1F4E79)' : 'var(--tx-3, #6B7280)',
        fontSize: 11, fontWeight: isActive ? 700 : 500,
        textDecoration: 'none',
        borderTop: isActive ? '2px solid var(--pr-1, #1F4E79)' : '2px solid transparent',
      })}
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
